import { Client } from "@stomp/stompjs";
import SockJS from "sockjs-client";
import { WS_URL } from "../config";
import { store } from "../../Store";
import { debugLog } from "../../Utils/utils";
import { CSPHelpers } from "../../Utils/security";

class ConnectionManager {
  constructor() {
    this.client = null;
    this.connected = false;
    this._connecting = false;
    this.currentChatId = null;
    this._messageHandlers = [];
    this._reconnectListeners = [];
    this._lastConnectedAt = null;
    this._connectedToken = null;
    this._reconnectAttempt = 0;
    this._healthCheckInterval = null;

    // Will be set by StompChatService after wiring
    this._subscriptionManager = null;

    this._setupVisibilityHandler();
    this._setupHealthCheck();
  }

  // --- Exponential backoff ---
  _getReconnectDelay() {
    const delay = Math.min(1000 * Math.pow(2, this._reconnectAttempt), 30000);
    this._reconnectAttempt++;
    return delay;
  }

  // --- Visibility change detection ---
  _setupVisibilityHandler() {
    if (typeof document === "undefined") return;

    document.addEventListener("visibilitychange", () => {
      if (document.visibilityState !== "visible") return;
      if (!this.client) return;

      if (!this.connected || !this.client.connected) {
        debugLog("[StompChat] Tab resumed, WebSocket dead — forcing reconnect");
        this._forceReconnect();
      } else {
        this._notifyReconnectListeners();
      }
    });
  }

  // --- Periodic health check ---
  _setupHealthCheck() {
    if (typeof setInterval === "undefined") return;
    this._healthCheckInterval = setInterval(() => {
      if (!this.client || !this.connected) return;
      if (!this.client.connected) {
        debugLog("[StompChat] Health check: STOMP client disconnected, reconnecting");
        this._forceReconnect();
      }
    }, 30000);
  }

  // Force a clean reconnect cycle
  _forceReconnect() {
    if (!this.client) return;
    this.connected = false;
    this._connecting = false;
    try {
      this.client.deactivate().then(() => {
        this.client.activate();
      });
    } catch (e) {
      this.client.activate();
    }
  }

  // Register a listener that fires after reconnect
  onReconnect(handler) {
    this._reconnectListeners.push(handler);
    return () => {
      this._reconnectListeners = this._reconnectListeners.filter((h) => h !== handler);
    };
  }

  _notifyReconnectListeners() {
    this._reconnectListeners.forEach((handler) => {
      try { handler(); } catch (e) { debugLog("Reconnect listener error:", e); }
    });
  }

  // Register a message handler (returns unsubscribe function)
  onMessage(handler) {
    this._messageHandlers.push(handler);
    return () => {
      this._messageHandlers = this._messageHandlers.filter((h) => h !== handler);
    };
  }

  // Notify all registered message handlers
  _notifyMessageHandlers(payload) {
    this._messageHandlers.forEach((handler) => {
      try {
        handler(payload);
      } catch (e) {
        debugLog("Error in message handler:", e);
      }
    });
  }

  // Connect to WebSocket server
  connect(jwtToken, onConnected, onMessage, onError) {
    if (this.connected && this.client) {
      const currentIsGuest = !this._connectedToken;
      const callerHasToken = !!jwtToken;
      if (currentIsGuest && callerHasToken) {
        this.disconnect();
      } else {
        if (onMessage) { this.onMessage(onMessage); }
        if (onConnected) { onConnected(); }
        return;
      }
    }

    if (this._connecting) {
      if (onMessage) { this.onMessage(onMessage); }
      return;
    }

    this._connecting = true;

    if (onMessage) { this.onMessage(onMessage); }

    const socketUrl = WS_URL;

    const connectHeaders = {
      session_id: CSPHelpers.generateNonce(),
      Chat: true,
      connectionType: "customerService",
    };
    if (jwtToken) {
      connectHeaders.Authorization = `Bearer ${jwtToken}`;
    }

    this.client = new Client({
      webSocketFactory: () => new SockJS(socketUrl),
      connectHeaders,
      reconnectDelay: this._getReconnectDelay(),
      heartbeatIncoming: 10000,
      heartbeatOutgoing: 10000,
      onConnect: (frame) => {
        const wasConnectedBefore = this._lastConnectedAt !== null;
        this.connected = true;
        this._connecting = false;
        this._lastConnectedAt = Date.now();
        // Reset backoff on successful connection
        this._reconnectAttempt = 0;
        // Reset reconnect delay to base for future disconnects
        if (this.client) {
          this.client.reconnectDelay = this._getReconnectDelay();
          // Reset again since _getReconnectDelay increments
          this._reconnectAttempt = 0;
        }

        const activeAuth = this.client.connectHeaders?.Authorization;
        this._connectedToken = activeAuth
          ? activeAuth.replace("Bearer ", "")
          : null;
        if (onConnected) { onConnected(frame); }

        if (wasConnectedBefore) {
          debugLog("[StompChat] Reconnected — notifying listeners to catch up on missed messages");
          setTimeout(() => this._notifyReconnectListeners(), 500);
        }

        const activeToken = jwtToken || store.getState().auth?.token;
        if (activeToken) {
          const currentUserId = store.getState().auth.user?.id;
          this.client.subscribe(
            `/socket-user/${currentUserId}/topic/chat`,
            (msg) => {
              const payload = JSON.parse(msg.body);
              if (
                payload.content ===
                "Sorry, I encountered an error processing your request."
              ) {
                return;
              }
              this.currentChatId = payload.chatId;
              this._notifyMessageHandlers(payload);
            }
          );

          this.client.subscribe(
            `/socket-user/${currentUserId}/topic/private`,
            (msg) => {
              const payload = JSON.parse(msg.body);
              if (
                payload.content ===
                "Sorry, I encountered an error processing your request."
              ) {
                return;
              }
              this._notifyMessageHandlers(payload);
            }
          );
        }

        // Delegate subscription restoration to SubscriptionManager
        if (this._subscriptionManager) {
          this._subscriptionManager._restoreSubscriptions(this);
        }
      },
      onStompError: (frame) => {
        debugLog("STOMP Error:", frame);

        if (frame.command === "ERROR") {
          const errorMessage = frame.headers?.message || "";

          if (errorMessage.includes("clientInboundChannel")) {
            debugLog(
              "clientInboundChannel error (stale session), reconnecting with fresh token:",
              errorMessage
            );
            this.connected = false;
            this._connecting = false;
            this.client.reconnectDelay = 0;
            const freshToken = store.getState().auth?.token;
            this.client.deactivate().then(() => {
              if (freshToken) {
                this.client.connectHeaders = {
                  ...this.client.connectHeaders,
                  Authorization: `Bearer ${freshToken}`,
                };
                this.client.reconnectDelay = this._getReconnectDelay();
                this._reconnectAttempt = 0;
                this.client.activate();
              }
            });
            return;
          }

          if (
            errorMessage.includes("subscription") ||
            errorMessage.includes("authentication") ||
            errorMessage.includes("authorization") ||
            errorMessage.includes("group") ||
            errorMessage.includes("topic")
          ) {
            debugLog(
              "Subscription/Auth/Group/Topic error, will retry on next attempt:",
              errorMessage
            );
            return;
          }
        }

        if (onError) { onError(frame); }
      },
      onWebSocketClose: () => {
        this.connected = false;
        this._connecting = false;
        debugLog("WebSocket connection closed");
      },
      onWebSocketError: (error) => {
        debugLog("WebSocket Error:", error);
      },
    });

    this.client.activate();
  }

  isConnected() {
    return this.connected && this.client;
  }

  getConnectionStatus() {
    const subCount = this._subscriptionManager
      ? Object.keys(this._subscriptionManager.subscriptions).length
      : 0;
    return {
      connected: this.connected,
      hasClient: !!this.client,
      subscriptions: subCount,
    };
  }

  disconnect() {
    if (this.client) {
      if (this._subscriptionManager) {
        this._subscriptionManager._unsubscribeAll();
      }
      this.client.deactivate();
      this.connected = false;
      this._connectedToken = null;
      this._messageHandlers = [];
      debugLog("Disconnected from WebSocket");
    }
  }
}

export default ConnectionManager;
